import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Modal,
  Button,
  Input,
  Form,
  Notification,
  Spin,
  Switch,
  DatePicker,
  TimePicker,
  Select,
} from "@arco-design/web-react";
import { IconAttachment, IconImage } from "@arco-design/web-react/icon";
import { EmojiClickData } from "emoji-picker-react";
import dayjs, { Dayjs } from "dayjs";

import IconFace from "../../../../../assets/icons/IconFace";
import {
  MAX_IMAGES_COUNT,
  MAX_POST_LENGTH,
  MAX_TITLE_LENGTH,
  SUPPORTED_DOCUMENT_TYPES,
  SUPPORTED_IMAGE_TYPES,
} from "../constants";

import { getButtonColor, getButtonTextColor } from "../../../../../utils/cssVariables";
import { LoggedUser } from "../../../../../models/Person";
import { createNewPostData } from "../../../../../utils/createNewPostData";
import { validatePostAttachments } from "../../../../../utils/validators/validatePostAttachments";
import {
  combineScheduleToUtcISO,
  getBrowserTimezone,
  getPreferredEventTimezoneOptions,
} from "../../../../../utils/scheduleDateTime";
import { PostAttachmentsPreview } from "./PostAttachments/PostAttachmentsPreview";
import { EmojiPickerModal } from "../../../../../components/EmojiPickerModal/EmojiPickerModal";

interface ICreatePostModalProps {
  user: LoggedUser | null;
  isPostCreating: boolean;
  onCreatePost: (newPost: FormData) => void;
}

export interface PostFormValues {
  heading: string;
  content: string;
  send_sms_notification?: boolean;
  schedule_for_later?: boolean;
  schedule_date?: string | Dayjs;
  schedule_time?: string | Dayjs;
  schedule_timezone?: string;
}

interface PostAttachments {
  documentFile?: File;
  imageFiles: File[];
}

const CreatePostModal = ({ user, isPostCreating, onCreatePost }: ICreatePostModalProps) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<PostFormValues>();
  const [visible, setVisible] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const scheduleEnabled = Form.useWatch("schedule_for_later", form);
  const timezoneOptions = useMemo(() => getPreferredEventTimezoneOptions(), []);

  const contentInputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const [attachments, setAttachments] = useState<PostAttachments>({
    documentFile: undefined,
    imageFiles: [],
  });

  const initialFormValues = {
    heading: "",
    content: "",
    send_sms_notification: false,
    schedule_for_later: false,
    schedule_date: undefined,
    schedule_time: undefined,
    schedule_timezone: getBrowserTimezone(),
  };

  const showModal = () => {
    setVisible(true);
  };

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    const content = form.getFieldValue("content");
    form.setFieldValue("content", content + emojiData.emoji);
    setShowEmojiPicker(false);

    contentInputRef.current?.focus();
  };

  const toggleEmojiPicker = () => {
    setShowEmojiPicker(!showEmojiPicker);
  };

  const handleDocumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0]) return;

    const error = validatePostAttachments(e.target.files[0], false, t);
    if (error) {
      Notification.error({ content: error });
      return;
    }

    setAttachments({
      imageFiles: attachments.imageFiles,
      documentFile: e.target.files[0],
    });

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    const newImages = Array.from(e.target.files);
    const validImages: File[] = [];

    for (const file of newImages) {
      const error = validatePostAttachments(file, true, t);
      if (error) {
        Notification.error({ content: `${file.name}: ${error}` });
        continue;
      }
      validImages.push(file);
    }

    if (attachments.imageFiles.length + validImages.length > MAX_IMAGES_COUNT) {
      Notification.warning({
        content: t("form_validation_messages.max_images_exceeded", {
          count: MAX_IMAGES_COUNT,
        }),
      });
      return;
    }

    setAttachments((prev) => ({
      ...prev,
      imageFiles: [...prev.imageFiles, ...validImages].slice(0, MAX_IMAGES_COUNT),
    }));

    if (imageInputRef.current) imageInputRef.current.value = "";
  };

  const removeAttachment = (type: keyof PostAttachments, value?: string) => {
    setAttachments((prev) => {
      if (type === "documentFile") {
        return { ...prev, documentFile: undefined };
      }
      if (type === "imageFiles" && value) {
        return {
          ...prev,
          imageFiles: prev.imageFiles.filter((file) => file.name !== value),
        };
      }
      return prev;
    });

    if (type === "documentFile" && fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleCreate = (formValues: PostFormValues) => {
    if (!user) return;

    let publishAt: string | undefined;
    if (formValues.schedule_for_later) {
      publishAt = combineScheduleToUtcISO(
        formValues.schedule_date,
        formValues.schedule_time,
        formValues.schedule_timezone || getBrowserTimezone()
      );
      if (!publishAt) {
        Notification.error({ content: t("home_page.schedule_datetime_required") });
        return;
      }
      if (!dayjs(publishAt).isAfter(dayjs())) {
        Notification.error({ content: t("home_page.schedule_datetime_future") });
        return;
      }
    }

    const formData = createNewPostData({
      user,
      heading: formValues.heading,
      content: formValues.content,
      documentFile: attachments.documentFile,
      imageFiles: attachments.imageFiles,
      send_sms_notification: formValues.send_sms_notification,
      publish_at: publishAt,
    });

    onCreatePost(formData);
    resetForm();
  };

  const handleCancel = () => {
    if (!isPostCreating) {
      resetForm();
      setVisible(false);
    }
  };

  const resetForm = () => {
    form.resetFields();
    setAttachments({
      documentFile: undefined,
      imageFiles: [],
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (imageInputRef.current) imageInputRef.current.value = "";
  };

  useEffect(() => {
    setVisible(isPostCreating);
  }, [isPostCreating]);

  const disablePastDates = (current: Dayjs) => {
    return current && current.isBefore(dayjs().startOf("day"));
  };

  return (
    <div>
      <Button
        style={{
          color: getButtonTextColor(),
          backgroundColor: getButtonColor(),
        }}
        size={"large"}
        shape="round"
        onClick={showModal}
      >
        {t("home_page.create_post")}
      </Button>

      <Modal
        className="feed-modal"
        title={t("home_page.create_post")}
        visible={visible}
        footer={null}
        onCancel={handleCancel}
      >
        {isPostCreating ? (
          <Spin className="feed-modal__loader" size={24} dot />
        ) : (
          <Form
            form={form}
            layout="vertical"
            initialValues={initialFormValues}
            onSubmit={handleCreate}
            autoComplete="off"
          >
            <Form.Item
              label={t("home_page.post_title_label")}
              field="heading"
              rules={[
                { required: true, message: t("notification_messages.post_title_empty") },
                {
                  max: MAX_TITLE_LENGTH,
                  message: t("notification_messages.post_title_length_exceeded"),
                },
              ]}
            >
              <Input
                className="post-title__input"
                placeholder={t("home_page.post_title_placeholder")}
                maxLength={MAX_TITLE_LENGTH}
                showWordLimit
              />
            </Form.Item>
            <Form.Item
              label={t("home_page.post_content_label")}
              field="content"
              rules={[
                { required: true, message: t("notification_messages.post_content_empty") },
                {
                  max: MAX_POST_LENGTH,
                  message: t("notification_messages.post_content_length_exceeded"),
                },
              ]}
            >
              <Input.TextArea
                className="post-content__input"
                placeholder={t("home_page.post_content_placeholder")}
                rows={5}
                showWordLimit
                maxLength={MAX_POST_LENGTH}
              />
            </Form.Item>
            <PostAttachmentsPreview
              documentFile={attachments.documentFile}
              imageFiles={attachments.imageFiles}
              onRemoveDocumentFile={() => removeAttachment("documentFile")}
              onRemoveImageFile={(name) => removeAttachment("imageFiles", name)}
              onAddImageFile={() => imageInputRef.current?.click()}
              onRemoveDocument={() => {}}
              onRemoveImage={() => {}}
            />
            {user?.isAdmin && (
              <div className="post-sms-notification">
                <div className="post-sms-notification__row">
                  <div className="post-sms-notification__copy">
                    <span className="post-sms-notification__label">
                      {t("home_page.send_sms_notification")}
                    </span>
                    <span className="post-sms-notification__help">
                      {t("home_page.send_sms_notification_help")}
                    </span>
                  </div>
                  <Form.Item field="send_sms_notification" triggerPropName="checked" noStyle>
                    <Switch className="post-sms-notification__switch" />
                  </Form.Item>
                </div>
              </div>
            )}
            {user?.isAdmin && (
              <div className="post-sms-notification post-schedule">
                <div className="post-sms-notification__row">
                  <div className="post-sms-notification__copy">
                    <span className="post-sms-notification__label">
                      {t("home_page.schedule_for_later")}
                    </span>
                    <span className="post-sms-notification__help">
                      {t("home_page.schedule_for_later_help")}
                    </span>
                  </div>
                  <Form.Item field="schedule_for_later" triggerPropName="checked" noStyle>
                    <Switch className="post-sms-notification__switch" />
                  </Form.Item>
                </div>
                {scheduleEnabled && (
                  <div className="post-schedule__pickers">
                    <Form.Item
                      label={t("home_page.schedule_date")}
                      field="schedule_date"
                      rules={[
                        {
                          required: true,
                          message: t("home_page.schedule_datetime_required"),
                        },
                      ]}
                    >
                      <DatePicker
                        format="MMMM D, YYYY"
                        placeholder={t("home_page.schedule_date_placeholder")}
                        style={{ width: "100%" }}
                        disabledDate={disablePastDates}
                        getPopupContainer={(node) => node.parentElement || document.body}
                      />
                    </Form.Item>
                    <Form.Item
                      label={t("home_page.schedule_time")}
                      field="schedule_time"
                      rules={[
                        {
                          required: true,
                          message: t("home_page.schedule_datetime_required"),
                        },
                      ]}
                    >
                      <TimePicker
                        use12Hours
                        format="h:mm A"
                        placeholder={t("home_page.schedule_time_placeholder")}
                        style={{ width: "100%" }}
                        disableConfirm
                        step={{ hour: 1, minute: 15 }}
                        getPopupContainer={(node) => node.parentElement || document.body}
                      />
                    </Form.Item>
                    <Form.Item
                      label={t("home_page.schedule_timezone")}
                      field="schedule_timezone"
                      rules={[
                        {
                          required: true,
                          message: t("home_page.schedule_datetime_required"),
                        },
                      ]}
                    >
                      <Select
                        showSearch
                        placeholder={t("home_page.schedule_timezone_placeholder")}
                        options={timezoneOptions}
                        style={{ width: "100%" }}
                        filterOption={(input, option) => {
                          const opt = option as { value?: string; label?: string };
                          const haystack = `${opt.value ?? ""} ${opt.label ?? ""}`.toLowerCase();
                          return haystack.includes(input.toLowerCase());
                        }}
                        getPopupContainer={(node) => node.parentElement || document.body}
                      />
                    </Form.Item>
                  </div>
                )}
              </div>
            )}
            <div className="post-actions__wrapper">
              <div className="post-actions__content">
                <Button className="post-action-button" onClick={toggleEmojiPicker}>
                  <IconFace className="post-action-button-icon" />
                </Button>
                <Form.Item style={{ margin: 0 }}>
                  {
                    <Button
                      className="post-action-button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={!!attachments.documentFile}
                    >
                      <IconAttachment className="post-action-button-icon" />
                    </Button>
                  }
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleDocumentChange}
                    accept={SUPPORTED_DOCUMENT_TYPES.join(",")}
                    style={{ display: "none" }}
                  />
                </Form.Item>
                <Form.Item style={{ margin: 0 }}>
                  <Button
                    onClick={() => {
                      imageInputRef.current?.click();
                      imageInputRef.current!.value = "";
                    }}
                    icon={<IconImage />}
                    disabled={attachments.imageFiles.length >= MAX_IMAGES_COUNT}
                  >
                    {!!attachments.imageFiles.length &&
                      `(${attachments.imageFiles.length}/${MAX_IMAGES_COUNT})`}
                  </Button>
                  <input
                    type="file"
                    ref={imageInputRef}
                    onChange={handleImageChange}
                    accept={SUPPORTED_IMAGE_TYPES.join(",")}
                    multiple
                    style={{ display: "none" }}
                  />
                </Form.Item>
              </div>
              <Button
                htmlType="submit"
                className="post-actions__submit"
                style={{
                  color: getButtonTextColor(),
                  backgroundColor: getButtonColor(),
                }}
                shape="round"
              >
                {scheduleEnabled ? t("home_page.schedule_button") : t("common.post_button")}
              </Button>

              <EmojiPickerModal
                visible={showEmojiPicker}
                onClose={() => setShowEmojiPicker(false)}
                onEmojiClick={handleEmojiClick}
              />
            </div>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default CreatePostModal;
